/**
 * useCustomerPayment Hook
 * ahooks data fetching hook for customer payment data
 *
 * @see CustomerPaymentResponse in types/api/customer.ts
 */

import { useRequest, clearCache } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { customerApi } from '@/services/api/customer.api';
import type {
  CustomerPaymentResponse,
  CreateCustomerPaymentRequest,
  UpdateCustomerPaymentRequest,
} from '@/types/api/customer';

/**
 * Helper to invalidate payment cache for a customer
 */
const invalidateCustomerPaymentCache = (customerId: number) => {
  clearCache(`/api/customers/${customerId}/payment`);
};

/**
 * Hook to fetch payment data for a specific customer
 */
export function useCustomerPayment(
  customerId: number | null,
  config?: Options<CustomerPaymentResponse, []>
) {
  // Fetch payment data with ahooks
  const { data, error, loading, refresh } = useRequest(
    () => customerApi.getPayment(customerId!),
    {
      ready: !!customerId,
      cacheKey: customerId ? `/api/customers/${customerId}/payment` : undefined,
      staleTime: 30000, // 30 seconds
      ...config,
    }
  );

  return {
    paymentData: data ?? null,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook for customer payment data mutations (create, update)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function useCustomerPaymentMutations(customerId: number) {
  const createPaymentData = async (data: CreateCustomerPaymentRequest) => {
    const response = await customerApi.createPayment(customerId, data);
    // Invalidate cache to refresh payment data
    invalidateCustomerPaymentCache(customerId);
    return response;
  };

  const updatePaymentData = async (data: UpdateCustomerPaymentRequest) => {
    const response = await customerApi.updatePayment(customerId, data);
    // Invalidate cache to refresh payment data
    invalidateCustomerPaymentCache(customerId);
    return response;
  };

  return {
    createPaymentData,
    updatePaymentData,
  };
}

/**
 * Hook to check if payment data exists for a customer
 */
export function useHasCustomerPaymentData(customerId: number | null) {
  const { paymentData } = useCustomerPayment(customerId);
  return !!paymentData;
}
